import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo } from "react";
import dayjs from "dayjs";
import { removeUnderScoreWithUpperCase } from "../../../utils/removeUnderscore";
import { useGetAllSuppliers, useGetGrazeons } from "../queryHooks";
import { useOrdersStore } from "../store/useOrdersStore";

export type OrderTimelineEntry = {
  scope?: string;
  from?: string | null;
  to?: string;
  userId?: string;
  supplierId?: string;
  at?: string | Date;
};

type AuditTrailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  orderTimeline?: OrderTimelineEntry[] | null;
};

const GARAAZ_SCOPE = "GARAAZ_ORDER";
const SUPPLIER_SCOPE = "SUPPLIER_ORDER";

const formatStatus = (status?: string | null) => {
  if (!status) return "-";
  const leaf = status.includes(".") ? status.split(".").pop() : status;
  return removeUnderScoreWithUpperCase(leaf ?? status);
};

const formatCompactTime = (at?: string | Date) => {
  if (!at) return "-";
  const parsed = dayjs(at);
  return parsed.isValid() ? parsed.format("DD MMM, hh:mm A") : "-";
};

const idKey = (id?: string | null) => (id ? String(id) : "");

const AuditTrailModal = ({
  isOpen,
  onClose,
  orderTimeline,
}: AuditTrailModalProps) => {
  const { data: allSuppliers } = useGetAllSuppliers();
  const { data: grazeons } = useGetGrazeons();
  const orderHandlers = useOrdersStore((state) => state.orderHandlers);

  const userNameById = useMemo(() => {
    const map: Record<string, string> = {};

    orderHandlers?.forEach((handler: any) => {
      const user = handler?.user;
      const key = idKey(user?._id || handler?.userId || handler?._id);
      if (!key) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    const grazeonList = Array.isArray(grazeons?.data)
      ? grazeons.data
      : Array.isArray(grazeons)
        ? grazeons
        : [];
    grazeonList.forEach((grazeon: any) => {
      const user = grazeon?.user;
      const key = idKey(user?._id || grazeon?.userId);
      if (!key || map[key]) return;
      const name = [user?.firstName, user?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      if (name) map[key] = name;
    });

    return map;
  }, [orderHandlers, grazeons]);

  const supplierNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (allSuppliers || []).forEach((supplier: any) => {
      const key = idKey(supplier?._id);
      if (key && supplier?.businessName) {
        map[key] = supplier.businessName;
      }
    });
    return map;
  }, [allSuppliers]);

  const resolvePerformedBy = (entry: OrderTimelineEntry) => {
    const userName = userNameById[idKey(entry.userId)];
    const supplierName = supplierNameById[idKey(entry.supplierId)];

    if (userName && supplierName) {
      return `${userName} · ${supplierName}`;
    }
    if (userName) return userName;
    if (supplierName) return supplierName;
    if (entry.userId || entry.supplierId) return "Unknown";
    return "System";
  };

  const entries = useMemo(() => {
    if (!Array.isArray(orderTimeline)) return [];
    return [...orderTimeline].sort((a, b) => {
      const aTime = a?.at ? new Date(a.at).getTime() : 0;
      const bTime = b?.at ? new Date(b.at).getTime() : 0;
      return bTime - aTime;
    });
  }, [orderTimeline]);

  const renderEventCard = (
    entry: OrderTimelineEntry,
    idx: number,
    side: "left" | "right",
  ) => {
    const isGaraaz = side === "left";
    return (
      <Box
        key={`${entry.scope}-${entry.to}-${entry.at}-${idx}`}
        borderWidth="1px"
        borderColor={isGaraaz ? "teal.200" : "orange.200"}
        bg={isGaraaz ? "teal.50" : "orange.50"}
        borderRadius="sm"
        px={2}
        py={1}
        maxW="100%"
      >
        <Text
          fontWeight="semibold"
          fontSize="xs"
          lineHeight="1.2"
          title={`${formatStatus(entry.from)} → ${formatStatus(entry.to)}`}
        >
          {formatStatus(entry.from)} → {formatStatus(entry.to)}
        </Text>
        <Text fontSize="10px" color="gray.600" lineHeight="1.2">
          {resolvePerformedBy(entry)}
        </Text>
        <Text fontSize="10px" color="gray.500" lineHeight="1.2">
          {formatCompactTime(entry.at)}
        </Text>
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      scrollBehavior="inside"
      returnFocusOnClose={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader py={3} fontSize="md">
          Audit Trail
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody py={2}>
          {entries.length === 0 ? (
            <Text color="gray.500" fontSize="sm">
              No audit trail events available.
            </Text>
          ) : (
            <VStack align="stretch" spacing={0}>
              {entries.map((entry, idx) => {
                const isGaraaz = entry.scope === GARAAZ_SCOPE;
                const isSupplier = entry.scope === SUPPLIER_SCOPE;
                const showLeft = isGaraaz || !isSupplier;
                const showRight = isSupplier;

                return (
                  <Flex key={`row-${idx}`} align="stretch" minH="36px">
                    <Box flex={1} pr={2} pb={1.5}>
                      {showLeft && renderEventCard(entry, idx, "left")}
                    </Box>

                    <Flex
                      w="16px"
                      flexShrink={0}
                      direction="column"
                      align="center"
                      position="relative"
                    >
                      <Box
                        position="absolute"
                        top={0}
                        bottom={0}
                        w="1.5px"
                        bg="gray.300"
                      />
                      <Box
                        mt={1.5}
                        zIndex={1}
                        w="7px"
                        h="7px"
                        borderRadius="full"
                        bg={showLeft ? "teal.500" : "orange.500"}
                        borderWidth="1.5px"
                        borderColor="white"
                      />
                    </Flex>

                    <Box flex={1} pl={2} pb={1.5}>
                      {showRight && renderEventCard(entry, idx, "right")}
                    </Box>
                  </Flex>
                );
              })}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter py={2}>
          <Button colorScheme="blue" size="sm" onClick={onClose}>
            OK
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AuditTrailModal;
